import produce from "immer"
import {
  SEARCH_CHANGE_FILTER,
  SEARCH_CHANGE_PAGE,
  SEARCH_CHANGE_SORT,
  SEARCH_CHANGE_VALUE,
  SEARCH_CLEAR,
  SEARCH_CLEAR_FILTERS,
  SEARCH_LOAD_SUGGESTIONS,
  SEARCH_MAP_LOAD,
  SEARCH_SET_PRESERVE_STATE
} from "./search.actions"
import type {
  FilterGroup,
  ISearchState,
  SearchActions,
  SearchFilterAction,
  SearchResultState,
  SearchSortValue,
  SearchSuggestions
} from "./search.types"

export const searchInitialState: ISearchState = {
  searchValue: "",
  searchResults: {
    page: 0,
    pageSize: 30,
    count: 0,
    totalPages: 0,
    filterGroups: [],
    items: []
  },
  filters: {
    Type: ["Papier en digitaal"],
    Category: [],
    Regions: [],
    Theme: []
  },
  filterSelected: false,
  sortOrder: "mapNumber",
  suggestions: {
    suggestions: [],
    resultPreviews: []
  },
  preserve: false
}

export function searchReducer(currentState: ISearchState | undefined, action: SearchActions): ISearchState {
  const state = currentState ?? searchInitialState
  switch (action.type) {
    case SEARCH_CHANGE_VALUE: {
      return changeSearchValue(state, action.payload as string)
    }
    case SEARCH_MAP_LOAD: {
      return mapSearchResultLoad(state, action.payload as SearchResultState)
    }
    case SEARCH_CHANGE_FILTER: {
      return changeFilter(
        state,
        (action as SearchFilterAction).payload!.filterGroup as FilterGroup,
        (action as SearchFilterAction).payload!.filters as string[]
      )
    }
    case SEARCH_CHANGE_SORT: {
      return changeSort(state, action.payload as SearchSortValue)
    }
    case SEARCH_LOAD_SUGGESTIONS: {
      return setSuggestions(state, action.payload as SearchSuggestions)
    }
    case SEARCH_CHANGE_PAGE: {
      return changePage(state, action.payload as number)
    }
    case SEARCH_CLEAR: {
      return searchInitialState
    }
    case SEARCH_CLEAR_FILTERS: {
      return clearFilters(state)
    }
    case SEARCH_SET_PRESERVE_STATE: {
      return setSearchPreserveState(state, action.payload as boolean)
    }
    default: {
      return state
    }
  }
}

function changeSearchValue(currentState: ISearchState, searchValue: string): ISearchState {
  return produce(currentState, (draftState: ISearchState) => {
    draftState.searchValue = searchValue
  })
}

function mapSearchResultLoad(currentState: ISearchState, results: SearchResultState): ISearchState {
  return produce(currentState, (draftState: ISearchState) => {
    draftState.searchResults = results
  })
}

function changeFilter(currentState: ISearchState, filterGroup: FilterGroup, filters: string[]) {
  return produce(currentState, (draftState: ISearchState) => {
    draftState.filters[filterGroup.name] = filters
    const hasSelectedFilter: boolean =
      JSON.stringify(draftState.filters, null, 2) !== JSON.stringify(searchInitialState.filters, null, 2)

    draftState.filterSelected = hasSelectedFilter
  })
}

function changeSort(currentState: ISearchState, sortValue: SearchSortValue): ISearchState {
  return produce(currentState, (draftState: ISearchState) => {
    draftState.sortOrder = sortValue
  })
}

function clearFilters(currentState: ISearchState): ISearchState {
  return produce(currentState, (draftState: ISearchState) => {
    draftState.filters = searchInitialState.filters
    draftState.filterSelected = false
  })
}

function setSuggestions(currentState: ISearchState, suggestions: SearchSuggestions): ISearchState {
  return produce(currentState, (draftState: ISearchState) => {
    draftState.suggestions = suggestions
  })
}

function changePage(currentState: ISearchState, page: number): ISearchState {
  return produce(currentState, (draftState: ISearchState) => {
    draftState.searchResults.page = page
  })
}

function setSearchPreserveState(currentState: ISearchState, preserve: boolean): ISearchState {
  return produce(currentState, (draftState: ISearchState) => {
    draftState.preserve = preserve
  })
}
